import React from 'react';

/**
 * The art inside a monthly placement badge: a trophy on a calendar.
 *
 * The calendar is doing as much work as the trophy. The club runs a dozen of
 * these a year and a member ends up with a shelf of them, so the art has to say
 * which competition this was. A bare trophy would look the same as every other
 * award the club might ever hand out.
 *
 * Drawn rather than shipped as an image file, which is why it has no background
 * to remove. It also stays sharp from the 48px it renders at on a narrow phone up
 * to whatever a detail view gives it, and a page already fetching a board does not
 * additionally fetch a picture.
 *
 * Deliberately carries no date and no number. A date on a monthly award invites
 * the question of what happened that day, and a numeral on the cup would say
 * "1" on the badge given to fourth place.
 *
 * The trophy stays gold on all four placements. The medal carries the placement
 * in its rim, ribbon and glow, and a silver trophy inside a silver shield reads
 * as one flat shape rather than as second place.
 *
 * `wordmark` is off by default and exists for the sizes where it can be read.
 * The art renders into a box 60% of the badge's width, so at four-across on a
 * phone it is about 40px wide and the word would be roughly three pixels a
 * letter. Turn it on where the badge is given real room.
 */
function MonthlyTrophyArt({ wordmark = false }) {
  const NAVY = '#33507A';
  const GOLD_EDGE = '#D9922A';

  return (
    <svg viewBox="0 0 512 512" width="100%" height="100%" role="presentation" focusable="false">
      <g stroke={NAVY} strokeWidth="14" strokeLinejoin="round">
        {/* Page */}
        <rect x="24" y="54" width="420" height="404" rx="44" fill="#F6F9FF" />
        {/* Header band. Square along the bottom, following the page above it. */}
        <path d="M24 98a44 44 0 0 1 44-44h332a44 44 0 0 1 44 44v66H24z" fill="#43628D" />
      </g>

      {wordmark && (
        <text
          x="234"
          y="132"
          textAnchor="middle"
          fill="#FFFFFF"
          fontSize="54"
          fontWeight="800"
          letterSpacing="3"
          fontFamily="var(--ds-font-body), system-ui, sans-serif"
        >
          CALENDAR
        </text>
      )}

      {/* Binding rings, crossing the top edge */}
      <g fill="#FFFFFF" stroke={NAVY} strokeWidth="13">
        <rect x="67" y="10" width="26" height="76" rx="13" />
        <rect x="130" y="10" width="26" height="76" rx="13" />
        <rect x="193" y="10" width="26" height="76" rx="13" />
        <rect x="256" y="10" width="26" height="76" rx="13" />
        <rect x="319" y="10" width="26" height="76" rx="13" />
        <rect x="382" y="10" width="26" height="76" rx="13" />
      </g>

      {/* Handles, behind the bowl */}
      <g fill="none" stroke={GOLD_EDGE} strokeWidth="34" strokeLinecap="round">
        <path d="M278 216c-38-6-54 12-54 36 0 26 22 40 54 44" />
        <path d="M426 216c38-6 54 12 54 36 0 26-22 40-54 44" />
      </g>
      <g fill="none" stroke="#F7C544" strokeWidth="16" strokeLinecap="round">
        <path d="M278 216c-38-6-54 12-54 36 0 26 22 40 54 44" />
        <path d="M426 216c38-6 54 12 54 36 0 26-22 40-54 44" />
      </g>

      <g stroke={GOLD_EDGE} strokeWidth="14" strokeLinejoin="round">
        {/* Foot, base flare, stem */}
        <rect x="296" y="394" width="112" height="26" rx="10" fill="#F7C544" />
        <path d="M316 372h72l14 26H302z" fill="#F7C544" />
        <rect x="334" y="338" width="36" height="38" fill="#F7C544" />
        {/* Bowl */}
        <path d="M276 204h152v54c0 52-34 86-76 86s-76-34-76-86z" fill="#F7C544" />
      </g>

      {/* Highlight down the left of the bowl, which is what stops the cup
          reading as a flat gold shape once the shield's own sheen passes over. */}
      <path d="M306 214h26v46c0 34 8 58 22 74-28-6-48-36-48-78z" fill="#FFDA6E" />

      {/* Ribbon across the foot */}
      <g stroke="#B92D20" strokeWidth="13" strokeLinejoin="round">
        <path d="M252 392h44v52l-22-18-22 18z" fill="#EA4335" />
        <path d="M408 392h44v52l-22-18-22 18z" fill="#EA4335" />
        <rect x="288" y="388" width="128" height="46" rx="8" fill="#EA4335" />
      </g>
      <rect x="302" y="401" width="100" height="20" rx="4" fill="#F7C544" />

      {/* Sparkles */}
      <g fill="#FFFFFF">
        <path d="M462 140q5 23 26 28-21 5-26 28-5-23-26-28 21-5 26-28z" />
        <path d="M432 214q3 13 15 16-12 3-15 16-3-13-15-16 12-3 15-16z" />
      </g>
    </svg>
  );
}

export default MonthlyTrophyArt;
